import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../services/apiConfig';
import { fetchWithAuth, getUserActivitiesModern } from '../services/authService';

export default function Support() {
    const location = useLocation();
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [category, setCategory] = useState('GENERAL');
    const [priority, setPriority] = useState('NORMAL');
    const [feedback, setFeedback] = useState<{ text: string; error: boolean } | null>(null);
    const [sending, setSending] = useState(false);
    const [requests, setRequests] = useState<any[]>([]);
    const [loadingRequests, setLoadingRequests] = useState(false);
    const [dateFrom, setDateFrom] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
    const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
    const [sortField, setSortField] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [selectedActivity, setSelectedActivity] = useState<any | null>(null);

    const formatDate = (value?: string) => value ? new Date(value).toLocaleDateString('it-IT') : '-';

    const statusLabels: Record<string, string> = { OPEN: 'Aperta', IN_PROGRESS: 'In lavorazione', RESOLVED: 'Risolta', CLOSED: 'Chiusa' };
    const loadRequests = async () => {
        setLoadingRequests(true);
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/user/auth/support`);
            if (response.ok) setRequests((await response.json()).requests || []);
        } finally {
            setLoadingRequests(false);
        }
    };

    useEffect(() => {
        loadRequests();
        const activityId = sessionStorage.getItem('selectedActivityId');
        if (activityId) {
            getUserActivitiesModern()
                .then(data => setSelectedActivity((data.activities || []).find((activity: any) => activity.activityId === activityId) || { activityId }))
                .catch(() => setSelectedActivity({ activityId }));
        }
    }, []);

    const toggleSort = (field: string) => {
        if (sortField === field) setSortDirection(direction => direction === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDirection('asc'); }
    };

    const visibleRequests = requests.filter(request => {
        const createdAt = new Date(request.createdAt || 0).getTime();
        const from = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : -Infinity;
        const to = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : Infinity;
        return createdAt >= from && createdAt <= to;
    }).sort((first, second) => String(first[sortField] || '').localeCompare(String(second[sortField] || ''), 'it', { numeric: true }) * (sortDirection === 'asc' ? 1 : -1));

    const getSupportContext = () => {
        const activityId = sessionStorage.getItem('selectedActivityId') || undefined;
        const workMatch = location.pathname.match(/^\/work-detail\/([^/]+)/);
        const workId = workMatch ? decodeURIComponent(workMatch[1]) : undefined;

        return {
            activityId,
            workId
        };
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setSending(true);
        setFeedback(null);
        try {
            const context = getSupportContext();
            const response = await fetchWithAuth(`${API_BASE_URL}/user/auth/support`, {
                method: 'POST',
                body: JSON.stringify({ subject, message, category, priority, ...context })
            });
            if (!response.ok) throw new Error('Invio non riuscito');
            setSubject('');
            setMessage('');
            setCategory('GENERAL');
            setPriority('NORMAL');
            setFeedback({ text: 'Richiesta inviata correttamente.', error: false });
            await loadRequests();
        } catch (error) {
            setFeedback({ text: 'Impossibile inviare la richiesta. Riprova più tardi.', error: true });
        } finally {
            setSending(false);
        }
    };

    return <Box sx={{ width: '100%', maxWidth: 1400, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(360px, 0.85fr) minmax(0, 1.6fr)' }, gap: 3, alignItems: 'start' }}>
        <Paper component="form" onSubmit={handleSubmit} sx={{
            p: { xs: 2, sm: 2.5 },
            display: 'grid',
            gap: 2,
            borderRadius: 1.5,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)'
        }}>
            {feedback && <Alert severity={feedback.error ? 'error' : 'success'}>{feedback.text}</Alert>}
            <Alert severity={selectedActivity ? 'info' : 'warning'}>
                {selectedActivity ? `Attività selezionata: ${selectedActivity.indirizzo || 'Attività'}${selectedActivity.citta ? `, ${selectedActivity.citta}` : ''} (indice: ${selectedActivity.activityId})` : 'Nessuna attività selezionata: seleziona un’attività prima di inviare la richiesta.'}
            </Alert>
            <TextField required label="Oggetto" value={subject} onChange={event => setSubject(event.target.value)} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <FormControl required>
                    <InputLabel>Categoria</InputLabel>
                    <Select value={category} label="Categoria" onChange={event => setCategory(event.target.value)}>
                        <MenuItem value="ACCOUNT">Account</MenuItem>
                        <MenuItem value="PAYMENT">Pagamenti</MenuItem>
                        <MenuItem value="DOCUMENTS">Documenti</MenuItem>
                        <MenuItem value="PRACTICES">Pratiche</MenuItem>
                        <MenuItem value="TECHNICAL">Problema tecnico</MenuItem>
                        <MenuItem value="GENERAL">Generale</MenuItem>
                    </Select>
                </FormControl>
                <FormControl required>
                    <InputLabel>Priorita</InputLabel>
                    <Select value={priority} label="Priorita" onChange={event => setPriority(event.target.value)}>
                        <MenuItem value="LOW">Bassa</MenuItem>
                        <MenuItem value="NORMAL">Normale</MenuItem>
                        <MenuItem value="HIGH">Alta</MenuItem>
                        <MenuItem value="URGENT">Urgente</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <TextField required multiline minRows={5} label="Messaggio" value={message} onChange={event => setMessage(event.target.value)} />
            <Button type="submit" variant="contained" disabled={sending || !selectedActivity} sx={{ py: 1.15, fontWeight: 600 }}>
                {sending ? 'Invio...' : 'Invia richiesta'}
            </Button>
        </Paper>
        <Box sx={{ minWidth: 0, minHeight: 0 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>Le mie richieste</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                <TextField size="small" type="date" label="Da" value={dateFrom} onChange={event => setDateFrom(event.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="A" value={dateTo} onChange={event => setDateTo(event.target.value)} InputLabelProps={{ shrink: true }} />
            </Box>
            <Box sx={{ maxHeight: 430, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {loadingRequests ? <Typography color="text.secondary">Caricamento...</Typography> : visibleRequests.length === 0 ? <Typography color="text.secondary">Nessuna richiesta nel periodo selezionato.</Typography> : (
                    <Table size="small" stickyHeader>
                        <TableHead><TableRow><TableCell><TableSortLabel active={sortField === 'createdAt'} direction={sortField === 'createdAt' ? sortDirection : 'asc'} onClick={() => toggleSort('createdAt')}>Data</TableSortLabel></TableCell><TableCell><TableSortLabel active={sortField === 'subject'} direction={sortField === 'subject' ? sortDirection : 'asc'} onClick={() => toggleSort('subject')}>Oggetto</TableSortLabel></TableCell><TableCell><TableSortLabel active={sortField === 'priority'} direction={sortField === 'priority' ? sortDirection : 'asc'} onClick={() => toggleSort('priority')}>Priorita</TableSortLabel></TableCell><TableCell><TableSortLabel active={sortField === 'status'} direction={sortField === 'status' ? sortDirection : 'asc'} onClick={() => toggleSort('status')}>Stato</TableSortLabel></TableCell><TableCell>Risposta amministratore</TableCell></TableRow></TableHead>
                        <TableBody>{visibleRequests.map(request => <TableRow key={request.requestId} hover><TableCell>{formatDate(request.createdAt)}</TableCell><TableCell><Typography fontWeight={600}>{request.subject}</Typography><Typography variant="caption" color="text.secondary">{request.message}</Typography></TableCell><TableCell>{request.priority}</TableCell><TableCell><Chip size="small" label={statusLabels[request.status] || request.status} color={request.status === 'RESOLVED' ? 'success' : request.status === 'IN_PROGRESS' ? 'warning' : 'info'} /></TableCell><TableCell>{request.adminNote || 'In attesa di risposta'}</TableCell></TableRow>)}</TableBody>
                    </Table>
                )}
            </Box>
        </Box>
    </Box>;
}
